import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, HomeIcon, SearchIcon, ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { LegalFooter } from '../components/LegalFooter';
interface PartnerBranding {
  name: string;
  logo: string;
  primaryColor: string;
  accentColor: string;
  backUrl: string;
  programTitle?: string;
}
export function ThankYouPage() {
  usePageMeta('Thank You | OnlineCollegeGuide', 'Your information request has been submitted. A school representative will be in touch shortly.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as any)?.formData;
  const partner = (location.state as any)?.partner as PartnerBranding | undefined;
  if (partner) {
    return <main className="min-h-screen flex flex-col" style={{
      backgroundColor: '#F8FAFB'
    }} data-id="element-2902" data-mp="3mt5qbyc2c5i5">
        {/* Partner Header */}
        <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm border-b border-gray-100" data-id="element-2903" data-mp="3rsidkk6ujel9">
          {partner.logo ? <img src={partner.logo} alt={`${partner.name} Logo`} className="h-14 object-contain" data-id="element-2904" data-mp="3vrhg5y53uz9j" /> : <span className="text-xl font-bold" style={{
          color: partner.primaryColor
        }} data-id="element-2905" data-mp="4gr04kurouqx">
              {partner.name}
            </span>}
        </header>

        {/* Hero */}
        <div className="relative overflow-hidden py-16 md:py-24" style={{
        background: `linear-gradient(135deg, ${partner.primaryColor} 0%, ${partner.primaryColor}dd 60%, ${partner.accentColor}88 100%)`
      }} data-id="element-2906" data-mp="eex3l6w828xw">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2907" data-mp="2l3a2h8hrlt4p">
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.1
          }} className="mx-auto mb-6" data-id="element-2908" data-mp="2l5mpw16akvp5">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto border-2" style={{
              backgroundColor: `${partner.accentColor}20`,
              borderColor: `${partner.accentColor}40`
            }} data-id="element-2909" data-mp="5athxa5d27v7">
                <CheckCircleIcon className="w-10 h-10" style={{
                color: partner.accentColor
              }} data-id="element-2910" data-mp="f7ogtu5jqaa0" />
              </div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2911" data-mp="ozc4rl2w996q">
              Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="text-lg text-white/90 mb-2" data-id="element-2912" data-mp="1cmfyxcw02dta">
              Your information has been submitted to{' '}
              <strong className="text-white" data-id="element-2913" data-mp="17ncx01bbgnpj">{partner.name}</strong>.
            </motion.p>

            {partner.programTitle && <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-base text-white/70" data-id="element-2914" data-mp="tvg4hw5qkbsm">
                Program: {partner.programTitle}
              </motion.p>}

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} className="text-base text-white/70 max-w-md mx-auto mt-3" data-id="element-2915" data-mp="oxwsz40fad4d">
              A representative from {partner.name} will reach out to you shortly
              to discuss your education options.
            </motion.p>
          </div>
        </div>

        {/* Steps Card */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16 w-full" data-id="element-2916" data-mp="3uatx26rdzyrx">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.55
        }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2917" data-mp="3q9qdpaa4rkly">
            <h2 className="text-lg font-bold mb-4" style={{
            color: partner.primaryColor
          }} data-id="element-2918" data-mp="3gaj3ux2lu83l">
              What Happens Next?
            </h2>
            <div className="space-y-4" data-id="element-2919" data-mp="37aw0i5qbes7w">
              <div className="flex items-start gap-4" data-id="element-2920" data-mp="37bxkx0t80r2a">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2921" data-mp="19kkxk59nfahi">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2922" data-mp="2nh4dvwdf8ijd">
                    1
                  </span>
                </div>
                <div data-id="element-2923" data-mp="2x6xrf2x4j7q9">
                  <p className="font-semibold text-gray-800" data-id="element-2924" data-mp="2x6fm7gshhj67">
                    Application Review
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2925" data-mp="317reju2qsii3">
                    {partner.name} will review your information and match you
                    with the best program options.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2926" data-mp="2dioqa3dlrrem">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2927" data-mp="2dh58278dz6a7">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2928" data-mp="3oxbyaf6pzjsf">
                    2
                  </span>
                </div>
                <div data-id="element-2929" data-mp="1tt45t81kb8iu">
                  <p className="font-semibold text-gray-800" data-id="element-2930" data-mp="1twrzc770buxk">
                    Expect a Call or Email
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2931" data-mp="23smpdndk8sad">
                    An admissions advisor from {partner.name} will contact you
                    {formData?.email ? ` at ${formData.email}` : ''} to discuss
                    next steps.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2932" data-mp="165mltyojheqf">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2933" data-mp="1631gv0sy2iog">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2934" data-mp="1g1zb8kd1x6fd">
                    3
                  </span>
                </div>
                <div data-id="element-2935" data-mp="ned1svh577kl">
                  <p className="font-semibold text-gray-800" data-id="element-2936" data-mp="2z1ynbmqrveta">
                    Enroll & Get Started
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2937" data-mp="33t8dcog8gk5p">
                    Once you're ready, your advisor at {partner.name} will guide
                    you through the enrollment process.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2938" data-mp="3hsgdbjk7zway">
              <button onClick={() => navigate(partner.backUrl)} className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-md transition-colors text-sm" style={{
              backgroundColor: partner.primaryColor
            }} onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'} data-id="element-2939" data-mp="3hsznge3mpgef">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2940" data-mp="1xbl9gi0enkgd" />
                Back to {partner.name}
              </button>
              <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 border font-semibold rounded-md transition-colors text-sm" style={{
              borderColor: partner.primaryColor,
              color: partner.primaryColor
            }} data-id="element-2941" data-mp="22dahrpw8tk0r">
                <HomeIcon className="w-4 h-4" data-id="element-2942" data-mp="2k1w3tdv88b2r" />
                Explore More Schools
              </button>
            </div>
          </motion.div>
        </div>
      </main>;
  }
  // Default generic thank you page
  return <main className="flex-grow bg-light" data-id="element-2943" data-mp="3cp8o1hdx06sw">
      <div className="hero-bg relative overflow-hidden py-16 md:py-24" data-id="element-2944" data-mp="37qxi3hqqqgfy">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2945" data-mp="37ss28tu6u4iz">
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }} className="mx-auto mb-6" data-id="element-2946" data-mp="32xvzaynexwr3">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto border border-white/20" data-id="element-2947" data-mp="3qo8gl04jt5uc">
              <CheckCircleIcon className="w-10 h-10 text-gold" data-id="element-2948" data-mp="6tv6jc83nj4y" />
            </div>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2949" data-mp="bt834oorw4kq">
            Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35
        }} className="text-lg text-gray-300 mb-2" data-id="element-2950" data-mp="prfsobjhc3lv">
            Your information has been submitted successfully.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45
        }} className="text-base text-gray-400 max-w-md mx-auto" data-id="element-2951" data-mp="14gc0q8ljdgmh">
            A representative from one of our partner schools will reach out to
            you shortly to discuss your education options.
          </motion.p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16" data-id="element-2952" data-mp="9tseyagqrsxb">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.55
      }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2953" data-mp="et5adjloi8bi">
          <h2 className="text-lg font-bold text-navy mb-4" data-id="element-2954" data-mp="ntbmp25n5293">
            What Happens Next?
          </h2>
          <div className="space-y-4" data-id="element-2955" data-mp="st6auxy17dqg">
            <div className="flex items-start gap-4" data-id="element-2956" data-mp="ss4z0r96yn7o">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2957" data-mp="2plpxzidoj8gj">
                <span className="text-sm font-bold text-navy" data-id="element-2958" data-mp="2ulcbzu1c0sqe">1</span>
              </div>
              <div data-id="element-2959" data-mp="2n10bpsvi6rlf">
                <p className="font-semibold text-gray-800" data-id="element-2960" data-mp="2n1ju1ap2fhzz">
                  Review Your Matches
                </p>
                <p className="text-sm text-gray-600" data-id="element-2961" data-mp="2i8qowlpoq7nd">
                  Our system is matching you with accredited programs that fit
                  your goals and preferences.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2962" data-mp="35zcmfj22c9j4">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2963" data-mp="35z4d2155fmco">
                <span className="text-sm font-bold text-navy" data-id="element-2964" data-mp="30ygorn8b1jm5">2</span>
              </div>
              <div data-id="element-2965" data-mp="2vzlvtsgmieob">
                <p className="font-semibold text-gray-800" data-id="element-2966" data-mp="2r2dl2cxt1cx3">
                  Expect a Call or Email
                </p>
                <p className="text-sm text-gray-600" data-id="element-2967" data-mp="1kmt3ogjx7o7q">
                  An admissions advisor from a matched school will contact you
                  {formData?.email ? ` at ${formData.email}` : ''} to discuss
                  next steps.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2968" data-mp="bpuxlw0rqaxw">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2969" data-mp="bojs9rrocv4o">
                <span className="text-sm font-bold text-navy" data-id="element-2970" data-mp="3txbxrplezz5v">3</span>
              </div>
              <div data-id="element-2971" data-mp="2qrdcopv7pht">
                <p className="font-semibold text-gray-800" data-id="element-2972" data-mp="3k566ki2zllrq">
                  Enroll & Get Started
                </p>
                <p className="text-sm text-gray-600" data-id="element-2973" data-mp="3owegdf88b95f">
                  Once you've found the right fit, your advisor will guide you
                  through the enrollment process.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2974" data-mp="3g1gp0ckqc4g7">
            <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 bg-navy hover:bg-navy-light text-white font-semibold rounded-md transition-colors text-sm" data-id="element-2975" data-mp="317lj9ffvev98">
              <HomeIcon className="w-4 h-4" data-id="element-2976" data-mp="2mkd4y5r9m9t1" />
              Back to Home
            </button>
            <button onClick={() => navigate('/colleges')} className="flex items-center justify-center gap-2 px-6 py-3 border border-navy text-navy hover:bg-navy/5 font-semibold rounded-md transition-colors text-sm" data-id="element-2977" data-mp="2ij1jmcffcaqn">
              <SearchIcon className="w-4 h-4" data-id="element-2978" data-mp="2rh3kmrz5r3dx" />
              Browse More Schools
            </button>
          </div>
        </motion.div>
      </div>
      <LegalFooter data-id="element-2979" data-mp="31h42salajdgj" />
    </main>;
}